import React from 'react';
import {
  ModalEntry,
  ModalId,
  useModals,
  useModalStack,
} from '../../contexts/ModalControllerContext';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import MessagesModal from '../MessagesModal/MessagesModal';
import BuildingView from '../BuildingModal/BuildingView';
import ListingPage from '../../pages/ListingPage/ListingPage';
import MarketingConsentModal from '../MarketingConsentModal/MarketingConsentModal';
import LandingPageCouldBuyOrCouldSellModal from '../LandingPageCouldBuyOrCouldSellModal/LandingPageCouldBuyOrCouldSellModal';
import ProfileView from '../ProfileView/ProfileView';
import LandingMenuModal from '../AppContent/NewLandingPage/LandingMenuModal';
import SignupLoginModal from '../SignupLoginModal/SignupLoginModal';
import './ModalOutlet.scss';

/**
 * Renders every modal currently on the stack, in order, at increasing
 * z-index. Stack index 0 is bottom (z-index 2100), top of stack is front.
 *
 * To add a new modal to the controller: add its ModalId to the union in
 * ModalControllerContext.tsx, then register its component in
 * MODAL_RENDERERS below. Call sites use `useModals()` and then
 * `modals.open(id, props)` / `modals.replace(oldId, newId, props)`.
 */

type ModalRenderer = React.ComponentType<any>;

const MODAL_RENDERERS: Partial<Record<ModalId, ModalRenderer>> = {
  dms: MessagesModal,
  building: BuildingView,
  listing: ListingPage,
  profile: ProfileView,
  landingMenu: LandingMenuModal,
  marketingConsent: MarketingConsentModal,
  landingCouldBuyOrCouldSell: LandingPageCouldBuyOrCouldSellModal,
  signupLogin: SignupLoginModal,
};

// BASE_Z must sit above .landing-page-map (z-index 2001) so the first
// modal in the stack visually covers the fullscreen landing-page map.
const BASE_Z = 2100;
const Z_STEP = 10;

// Per-modal z-index overrides for modals that must escape the default
// stack ordering. Marketing consent is non-dismissable and session-once,
// so it has to sit above the auth modal and the 10000-tier lightboxes
// (MessagesImageLightbox, PriceEstimationReport) regardless of stack position.
// Each modal's owner keeps a visibility flag that is the source of truth, so a
// crashed modal must be closed through the owner's own handler (the prop named
// here, `onClose` when absent), not only by dropping it from the stack: the
// flag would stay "open" and the modal could never be opened again.
const OWNER_CLOSE_PROP: Partial<Record<ModalId, string>> = {
  dms: 'toggleMessagesModal',
  building: 'toggleShowBuildingModal',
  signupLogin: 'toggleSignupLoginModalVisible',
};

/**
 * Closes a crashed modal: runs the owner's close handler so its visibility
 * flag resets, then removes the modal from the stack in case the owner has none.
 */
const closeCrashedModal = (entry: ModalEntry, close: (id: ModalId) => void) => {
  const ownerClose = entry.props?.[OWNER_CLOSE_PROP[entry.id] ?? 'onClose'];
  try {
    if (typeof ownerClose === 'function') ownerClose();
  } finally {
    close(entry.id);
  }
};

const Z_INDEX_OVERRIDE: Partial<Record<ModalId, number>> = {
  marketingConsent: 10001,
  // Anything can demand a login, so the auth modal has to outrank every surface
  // that survives it opening. The nav tier does not qualify: `navVisible` in
  // AppContent excludes the auth-modal state, so the bottom nav and everything
  // gated behind it (Activity, Tools, the tool flows) is unmounted by the time
  // this renders. What does coexist is AddMyPlaceFlow (2402), the edit-listing
  // and off-market sheets (2520), CouldBuyFlow (2600) and the shared confirm
  // overlay several callers portal straight to <body> (2700). All direct-rendered,
  // so stack position alone would leave the auth modal underneath — which is
  // exactly what its old hardcoded 1202 did.
  signupLogin: 2800,
  // Profile is a full-attention account view; keep the global Jaakko nav under
  // its overlay on desktop so it doesn't look or behave interactively. Keep it
  // above direct-rendered nav modals such as ToolsSheet (z 2401).
  profile: 2500,
  // The header menu should always appear above the current nav modal/card.
  landingMenu: 2510,
};

export const ModalOutlet: React.FC = () => {
  const stack = useModalStack();
  const modals = useModals();

  if (stack.length === 0) return null;

  return (
    <>
      {stack.map((entry: ModalEntry, idx: number) => {
        const Component = MODAL_RENDERERS[entry.id];
        if (!Component) return null;
        const z = Z_INDEX_OVERRIDE[entry.id] ?? BASE_Z + idx * Z_STEP;
        return (
          <div
            key={entry.key}
            className="modal-outlet__layer"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: z,
            }}
          >
            <ErrorBoundary
              name={`modal:${entry.id}`}
              fallback={null}
              onError={() => closeCrashedModal(entry, modals.close)}
            >
              <Component {...entry.props} />
            </ErrorBoundary>
          </div>
        );
      })}
    </>
  );
};
